import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import ReactTable from 'react-table';
import 'react-table/react-table.css';

import Divider from 'material-ui/Divider';
import Badge from 'material-ui/Badge';
import IconButton from 'material-ui/IconButton';
import RightIcon from 'material-ui/svg-icons/navigation/chevron-right';
import ErrorIcon from 'material-ui/svg-icons/alert/error-outline';
import LeftIcon from 'material-ui/svg-icons/navigation/chevron-left';
import theme from '../../../muiTheme';
import { dataGridFiltersOperations } from '../../../redux/dataGridFilters';

import { getFieldType } from '../../../utils/helpers';
import {
  getAccessor,
  getCellDef,
  getActionsDef,
  getFilterComponent
} from './gridHelpers';

import './DataGrid.css';
import ColumnChooser from './ColumnChooser';
import moment from 'moment';

const styles = {
  wrapperStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  gridStyle: {
    flex: '1 0 200px',
    width: '100%',
    height: '100%',
    border: 'none',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
  },
  headerStyle: {
    margin: 0,
    fontWeight: 300
  },
  badgeStyle: {
    top: '1.55rem',
    right: '-.75rem',
    color: theme.palette.alternateTextColor
  },
  footerIconBtn: {
    width: '36px',
    height: '36px',
    padding: '6px'
  }
};

class DataGrid extends Component {
  constructor() {
    super();

    this.filtering = false;
    this.fetchDataWithDebounce = debounce(this.fetchData, 350);

    this.state = {
      initialized: false
    };
  }

  componentDidMount() {
    if (this.props.static) return;

    if (this.props.ready) {
      this.initialize(this.props.items, this.props.pollInterval);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.static) return;

    if (!this.state.initialized && this.props.ready) {
      this.initialize(this.props.items, this.props.pollInterval);
      this.ReactTable.fireFetchData();
    }

    // Handle anything that requires a forced refreshing of data
    if (prevProps.baseQuery !== this.props.baseQuery) {
      this.ReactTable.fireFetchData();
    }

    // Handle updating when map extent changes (if toggled by user)
    if (
      this.props.dataGridFilters.automaticSpatialQuery &&
      prevProps.map.currentExtent !== this.props.map.currentExtent
    ) {
      this.ReactTable.fireFetchData();
    }

    // If user toggles filtering incidents by map extent on/off, re-fetch data
    if (
      this.props.dataGridFilters.automaticSpatialQuery !==
      prevProps.dataGridFilters.automaticSpatialQuery
    ) {
      this.ReactTable.fireFetchData();
    }
  }

  initialize(items, pollInterval) {
    if (!items.length) {
      this.loadInitialItems();
    }

    if (pollInterval && pollInterval > 0) {
      this.setupPolling(pollInterval, this.ReactTable);
    }

    this.setState({ initialized: true });
  }

  setupPolling(interval, ReactTable) {
    this.interval = window.setInterval(() => {
      // This is an undocumented method in react-table that fires the fetchData
      // method with react-table's current state. If this causes any issues,
      // we should consider using react-table in a fully controlled manner so
      // that we maintain the table's state ourselves.
      // https://github.com/react-tools/react-table/blob/f74ba3cc1609f9f6b5e79e3f916b49d2b4bb22c4/src/methods.js#L338
      this.setState({ silent: true });
      ReactTable.fireFetchData();
    }, interval);
  }

  componentWillUnmount() {
    window.clearInterval(this.interval);
  }

  loadInitialItems() {
    this.props.onLoadStart();
  }

  handleTableUpdate = (tableState, instance) => {
    if (this.props.static) {
      this.getSearchOptions(tableState);
      return;
    }

    // Make sure component is 'ready'
    if (!this.props.ready) {
      this.props.onLoadStart();
      return null;
    }

    // If user is filtering, call a debounced reference to fetchData to avoid
    // pinging the server with requests as users type into the filter input
    if (this.filtering) {
      return this.fetchDataWithDebounce(tableState, instance);
    }

    return this.fetchData(tableState, instance);
  };

  fetchData = (tableState, instance) => {
    this.filtering = false;

    if (!this.state.silent) {
      this.props.onLoadStart();
    }
    this.setState({ silent: false });

    const options = this.getSearchOptions(tableState);

    this.props.fetchItems(options).then(
      results => {
        // remove queryError message if fetchItems was successful
        // this is necessary when queryError was previously set when fetchItems failed
        this.setState({ queryError: false });

        this.props.updateItems(
          results.featureSet.features,
          results.count,
          results.featureSet.exceededTransferLimit || false
        );
      },
      error => {
        console.error('Error loading items.', error);
        this.setState({ queryError: error.details.messages[0] });
        this.props.updateItems([], 0, false);
        this.props.onLoadComplete();
      }
    );
  };

  getSearchOptions(table) {
    const { pageSize, sorted, page } = table;
    const { baseQuery, fieldDefinitions } = this.props;

    const filtered = table.filtered.map(filter => {
      const type = getFieldType(
        fieldDefinitions.filter(d => d.name === filter.id)[0]
      );
      // if (filter.id === 'ModifiedOnDateTime') {
      //   const test = filter.value[0].unix();
      //   filter.value = test
      // }
      return { ...filter, type };
    });

    const extent = this.props.dataGridFilters.automaticSpatialQuery
      ? this.props.map.currentExtent
      : null;

    const isFilterAppliedToMap = this.props.isFilterAppliedToMap
      ? this.props.isFilterAppliedToMap
      : null;

    const searchOptions = {
      baseQuery,
      pageSize,
      page,
      filters: filtered,
      sort: sorted,
      extent,
      isFilterAppliedToMap
    };

    this.props.setFilter(searchOptions, this.props.api);

    return searchOptions;
  }

  getDataObjects(items) {
    return items.map(incident => ({ ...incident.attributes }));
  }

  onFilteredChange = (column, value) => {
    this.filtering = true;
  };

  getFieldFilter = (def, api) => {
    const searchOptions = this.props.dataGridFilters[api];

    if (searchOptions && searchOptions.filters.length) {
      return searchOptions.filters.filter(
        filterItem => filterItem.id === def.name
      )[0];
    }
  };

  getColumns = (
    fieldDefinitions,
    actions,
    attributeFormatter,
    api,
    fieldInfos
  ) => {
    // filter out non-existent columns, check because of 1.5.0 -> 1.6.0
    const columns = fieldDefinitions
      .filter(def => {
        if (def) {
          return true;
        }
        return false;
      })
      .map(def => {
        const fieldFilter = this.getFieldFilter(def, api);
        const disabled = def.disabled;
        const Header = def.alias;
        const width = def.width;
        const filterable = !(def.filterable === false);
        const id = def.name;
        const accessor = getAccessor(def, attributeFormatter, api, fieldInfos);
        const Cell = getCellDef(def, attributeFormatter, fieldInfos);
        const Filter = getFilterComponent(def, api, fieldFilter);
        return { Header, accessor, id, width, filterable, Cell, Filter };
      });

    if (!actions) {
      return columns;
    }

    const actionsCell = getActionsDef(actions);
    const actionsHeader = this.props.actionsHeader
      ? this.props.actionsHeader
      : '';
    const actionCol = {
      Header: actionsHeader,
      id: 'actions',
      Cell: actionsCell,
      filterable: false,
      sortable: false,
      resizable: false,
      headerClassName: 'actions-col',
      className: 'actions-col',
      width: 58,
      disabled: 'actions-col'
    };

    return [...columns, actionCol];
  };

  getPages(itemCount, pageSize) {
    const pages = Math.ceil(itemCount / pageSize);
    return pages;
  }

  getQueryErrorMessage(message) {
    if (!message) return 'No records found.';

    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: theme.palette.accent1Color }}>
          <ErrorIcon
            color={theme.palette.accent1Color}
            style={{ height: '150px', width: '150px' }}
          />
          <h3>There was an error fetching records.</h3>
        </div>
        <Divider />
        The following message was returned from the IRWIN API:
        <div style={{ fontFamily: 'monospace' }}>
          <strong>{message}</strong>
        </div>
      </div>
    );
  }

  nextComponent = props => {
    const { children, ...rest } = props;
    return (
      <IconButton
        style={styles.footerIconBtn}
        {...rest}
        tooltip={children}
        tooltipPosition="top-center"
      >
        <RightIcon />
      </IconButton>
    );
  };

  prevComponent = props => {
    const { children, ...rest } = props;
    return (
      <IconButton
        style={styles.footerIconBtn}
        {...rest}
        tooltip={children}
        tooltipPosition="top-center"
      >
        <LeftIcon />
      </IconButton>
    );
  };

  render() {
    const isStatic = this.props.static;
    const customFilterMethod = (filter, row) => {
      if (isStatic && filter.value[2] === 'date') {
        if (
          filter.value[0] === null ||
          filter.value[1] === null ||
          filter.value.length === 1
        ) {
          // Incomplete or cleared date picker
          return true;
        }

        if (
          moment(row[filter.id].props.datetime).isBetween(
            filter.value[0],
            filter.value[1]
          )
        ) {
          // Found row matching filter
          return true;
        }
      }
      if (isStatic && filter.value[2] !== 'date') {
        if (filter.value.length === 0) {
          return true;
        }
        if (
            typeof (filter.value) === 'string' && !row[filter.id].props) {
          if (
              filter.value[0] !== 0 &&
              filter.value[0] !== 1 &&
              row[filter.id].toLowerCase().search(filter.value.toLowerCase()) > -1
          ) {
            // Found row matching filter
            return true;
          }
        }
        if (row[filter.id].props && row[filter.id].props.children !== undefined) {
          if (filter.value[0] !== 0 &&
              filter.value[0] !== 1 &&
              row[filter.id].props.children.toLowerCase().search(filter.value.toLowerCase()) > -1
          ) {
            // Found row matching filter
            return true;
          }
        }
        if (
          filter.value.length !== 0 &&
          Array.isArray(filter.value) &&
          filter.value.includes(row[filter.id].toString())
        ) {
          // Found row matching filter
          return true;
        }
        if (
          filter.value.length !== 0 &&
          filter.value.includes(0) &&
          row[filter.id].toString() === 'false'
        ) {
          // Found row matching filter
          return true;
        }
        if (
          filter.value.length !== 0 &&
          filter.value.includes(1) &&
          row[filter.id].toString() === 'true'
        ) {
          // Found row matching filter
          return true;
        }

      }
    };
    const isResourceHistory = this.props.resourceHistory;
    const badgeColor =
      this.props.badgeColor || theme.palette.secondaryTextColor;
    let titleRow;
    if (this.props.title) {
      titleRow = (
        <Badge
          badgeStyle={{ ...styles.badgeStyle, backgroundColor: badgeColor }}
          badgeContent={this.props.itemCount}
        >
          <h2 style={styles.headerStyle}>{this.props.title}</h2>
        </Badge>
      );
    }

    return (
      <div style={{ ...styles.wrapperStyle, ...this.props.style }}>
        {titleRow}

        <ReactTable
          style={styles.gridStyle}
          ref={table => {
            this.ReactTable = table;
          }}
          noDataText={this.getQueryErrorMessage(this.state.queryError)}
          data={this.getDataObjects(this.props.items)}
          columns={this.getColumns(
            this.props.fieldDefinitions,
            this.props.actions,
            this.props.attributeFormatter,
            this.props.api,
            this.props.fieldInfos
          )}
          SubComponent={this.props.SubComponent}
          onFilteredChange={this.onFilteredChange}
          onFetchData={this.handleTableUpdate}
          defaultSorted={this.props.defaultSort}
          pages={
            !isStatic || isResourceHistory
              ? this.getPages(this.props.itemCount, this.props.pageSize)
              : 1
          }
          defaultPageSize={
            !isStatic || isResourceHistory
              ? this.props.pageSize
              : this.props.items.length
          }
          defaultFilterMethod={customFilterMethod || undefined}
          showPageSizeOptions={false}
          showPagination={!this.props.hidePagination}
          loading={this.props.loading}
          NextComponent={this.nextComponent}
          PreviousComponent={this.prevComponent}
          nextText="Next page"
          previousText="Previous page"
          manual={!isStatic}
          minRows={0}
          filterable
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  dataGridFilters: state.dataGridFilters,
  map: state.map
});

const mapDispatchToProps = function(dispatch) {
  return bindActionCreators(
    {
      ...dataGridFiltersOperations
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DataGrid);

DataGrid.propTypes = {
  ready: PropTypes.bool,
  title: PropTypes.string,
  style: PropTypes.object,
  badgeColor: PropTypes.string,
  items: PropTypes.array.isRequired,
  onLoadStart: PropTypes.func,
  fetchItems: PropTypes.func,
  updateItems: PropTypes.func,
  isMore: PropTypes.bool,
  fieldDefinitions: PropTypes.array.isRequired,
  attributeFormatter: PropTypes.func,
  itemCount: PropTypes.number,
  baseQuery: PropTypes.string,
  pageSize: PropTypes.number,
  defaultSort: PropTypes.array,
  loading: PropTypes.bool,
  pollInterval: PropTypes.number,
  SubComponent: PropTypes.object
};

DataGrid.defaultProps = {
  ready: true,
  loading: true,
  pageSize: 10,
  baseQuery: null,
  defaultSort: []
};



// WEBPACK FOOTER //
// ./src/components/common/DataGrid/DataGrid.js